import type {
  KnowledgeAnswer,
  KnowledgeBreakdownRow,
  KnowledgeContext,
  KnowledgeLink,
  KnowledgeQuery,
} from './knowledgeRegistry';
import type { GeoPoint } from './geocode';
import {
  searchMarketplaceLocations,
  type MarketplaceLocation,
} from './requests/location-search-requests';

/**
 * Searches the marketplace for prospective locations a user could book, by city
 * or ZIP, and lists their features.
 *
 * ## Why this skill exists
 *
 * Every other skill scopes to the *signed-in account's own* locations — the
 * supplier bulk-edit export. A marketplace question such as "what features does
 * this location have" or "do you have parking spaces in Long Beach" is about a
 * prospective listing, not one attached to the caller's own account, so this
 * answers from the public marketplace instead of the export.
 *
 * ## How it finds a place
 *
 * The marketplace endpoint only searches by coordinates, so a typed place — a
 * city, a ZIP — is geocoded first (via {@link KnowledgeContext.geocode}). When
 * the message names no searchable place, or the place cannot be geocoded, the
 * skill says so and asks for a city or ZIP rather than guessing or pretending
 * the account has no locations.
 */

/** How many matches to list under a search. Enough to be useful, short enough
 *  to read; the total is always stated so a larger result is not hidden. */
const MAX_LISTED = 6;

/** How many feature names to show per location before summarising the rest. */
const MAX_FEATURES = 4;

/** A 5-digit US ZIP anywhere in the message. */
const ZIP = /\b(\d{5})\b/;

/**
 * A place introduced by a locational preposition — "in Los Angeles", "near
 * Dallas TX", "around 90210". Anchored to the end so it captures the whole tail
 * of the sentence, which is where a place name sits.
 */
const AFTER_PREPOSITION = /\b(?:in|near|around|within|inside|close to|next to|by|at)\s+(.+)$/i;

/**
 * A place introduced by the phrasing a "features" question uses — "features of
 * the Dallas yard", "features for Chicago". Only consulted for feature
 * questions, and only after the preposition form, so it cannot mistake a verb
 * for a place in a plain search.
 */
const AFTER_FEATURE_LEADIN = /\b(?:features?|amenities|amenity)\b.*?\b(?:of|for|at)\s+(.+)$/i;

/** Trailing filler that is never part of a place name. */
const TRAILING_NOISE =
  /\b(?:please|right now|available|to book|for booking|thanks|thank you)\b/gi;

const mentionsFeatures = (message: string): boolean =>
  /\b(?:feature|features|amenity|amenities)\b/i.test(message);

const wantsParking = (message: string): boolean =>
  /\bparking\b/i.test(message);

const wantsInstantApproval = (message: string): boolean =>
  wantsParking(message) || /\binstant approval\b/i.test(message);

/** Tidies a captured place: drops quotes, trailing punctuation and filler. */
const cleanPlace = (raw: string): string =>
  raw
    .replace(/[?!.]+$/g, ' ')
    .replace(/["'\u201c\u201d\u2018\u2019]/g, ' ')
    .replace(TRAILING_NOISE, ' ')
    .replace(/\b(the|a|an)\b\s*$/i, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Pulls the place to search from the message, or null when it names none.
 *
 * A ZIP wins outright — it is unambiguous and often typed on its own ("find me
 * locations in 34741"). Otherwise the place is whatever follows a locational
 * preposition, falling back for feature questions to whatever follows "features
 * of". Naming no place at all returns null, which the skill turns into a request
 * for one rather than a wrong search.
 */
export const extractPlace = (message: string): string | null => {
  const zip = message.match(ZIP);
  if (zip) return zip[1];

  const prep = message.match(AFTER_PREPOSITION);
  if (prep) {
    const place = cleanPlace(prep[1]);
    if (place) return place;
  }

  if (mentionsFeatures(message)) {
    const lead = message.match(AFTER_FEATURE_LEADIN);
    if (lead) {
      const place = cleanPlace(lead[1]);
      if (place) return place;
    }
  }

  return null;
};

/** A location's display name — its own name, else the company behind it. */
const nameOf = (location: MarketplaceLocation): string =>
  location.locationName?.trim() || location.companyName?.trim() || 'Unnamed location';

/** "City, ST", "City", or '' when neither is known. */
const placeOf = (location: MarketplaceLocation): string =>
  [location.city?.trim(), location.state?.trim()].filter(Boolean).join(', ');

/** A short, human list of a location's features, or '' when it lists none. */
const featureSummary = (location: MarketplaceLocation): string => {
  const features = (location.features ?? []).map((f) => f.trim()).filter(Boolean);
  if (features.length === 0) return '';
  if (features.length <= MAX_FEATURES) return features.join(', ');
  return `${features.slice(0, MAX_FEATURES).join(', ')} and ${features.length - MAX_FEATURES} more`;
};

const equipmentSummary = (location: MarketplaceLocation): string => {
  const equipment = (location.equipmentTypes ?? []).map((item) => item.trim()).filter(Boolean);
  if (equipment.length === 0) return '';
  if (equipment.length <= 3) return equipment.join(', ');
  return `${equipment.slice(0, 3).join(', ')} and ${equipment.length - 3} more`;
};

const approvalSummary = (location: MarketplaceLocation): string =>
  location.hasInstantApproval ? 'Instant Approval' : 'Request Space';

const spacesPhrase = (location: MarketplaceLocation): string => {
  const spaces = location.totalNumberOfSpaces;
  if (typeof spaces === 'number' && Number.isFinite(spaces) && spaces > 0) {
    return `${spaces} ${spaces === 1 ? 'space' : 'spaces'}`;
  }
  return '';
};

/** One breakdown row for a location: where it is, its size, its features. */
const rowFor = (location: MarketplaceLocation): KnowledgeBreakdownRow => {
  const parts = [approvalSummary(location), placeOf(location), spacesPhrase(location)].filter(
    Boolean,
  );
  const equipment = equipmentSummary(location);
  if (equipment) parts.push(`equipment: ${equipment}`);
  const features = featureSummary(location);
  if (features) parts.push(`features: ${features}`);
  return {
    label: nameOf(location),
    value: parts.join(' \u00b7 ') || 'Details on its listing',
    ...(location.locationId
      ? { link: { label: 'View location', path: `/location-profile/${location.locationId}` } }
      : {}),
  };
};

const pluralLocations = (count: number): string =>
  `${count} location${count === 1 ? '' : 's'}`;

/** The in-app search page centred on the geocoded point, so the user can browse
 *  the same set on the map and refine dates, capacity and filters. */
const searchLink = (point: GeoPoint, label: string): KnowledgeLink => ({
  label,
  path: `/search?lat=${point.lat}&lng=${point.lon}`,
});

/**
 * The call-to-action offered under a search answer.
 *
 * One unambiguous match links straight to that location's profile — the next
 * thing the user wants is to see and book it. A cluster (or a lone match with no
 * id to link to) links to the search page instead, where the whole set is on the
 * map and can be filtered, rather than pretending one of many is *the* answer.
 */
const linkFor = (
  results: MarketplaceLocation[],
  point: GeoPoint,
): KnowledgeLink | undefined => {
  if (results.length === 0) return undefined;

  const only = results.length === 1 ? results[0] : undefined;
  if (only?.locationId) {
    return { label: 'View this location', path: `/location-profile/${only.locationId}` };
  }

  return searchLink(point, `See all ${pluralLocations(results.length)} on the map`);
};

/**
 * Answers the search once a place has been resolved to results.
 *
 * A feature question leads with the nearest match's features spelled out, since
 * that is what was asked; a plain "how many"/"find" leads with the count. Either
 * way the same breakdown of matches follows, so no answer hides the rest.
 */
const describeResults = (
  place: string,
  results: MarketplaceLocation[],
  wantsFeatures: boolean,
  wantsInstant: boolean,
  point: GeoPoint,
): KnowledgeAnswer => {
  if (results.length === 0) {
    const qualifier = wantsInstant ? 'Instant Approval ' : '';
    return {
      text:
        `I could not find any available ${qualifier}locations near **${place}** right now. ` +
        'Try a nearby city, a different ZIP, or a wider area \u2014 or browse the full ' +
        'map from the **Find A Space** search page.',
      link: searchLink(point, `Search Find A Space near ${place}`),
    };
  }

  const total = results.length;
  const shown = results.slice(0, MAX_LISTED);
  const breakdown = shown.map(rowFor);
  const link = linkFor(results, point);
  const more =
    total > shown.length
      ? ` Showing the first ${shown.length}; ask about a specific one for its full listing.`
      : '';

  if (wantsFeatures) {
    const top = results[0];
    const features = featureSummary(top);
    const featureText = features
      ? `**${nameOf(top)}**${placeOf(top) ? ` in ${placeOf(top)}` : ''} lists these features: ${features}.`
      : `**${nameOf(top)}**${placeOf(top) ? ` in ${placeOf(top)}` : ''} does not list any features on its marketplace listing.`;

    const others =
      total > 1
        ? ` I found ${pluralLocations(total)} near **${place}** \u2014 here is what each offers.${more}`
        : '';

    return { text: `${featureText}${others}`, breakdown, link };
  }

  if (wantsInstant) {
    return {
      text:
        `I found **${pluralLocations(total)}** with **Instant Approval** near **${place}**.` +
        `${more} Here are the active yards I found:`,
      breakdown,
      link: searchLink(point, `Search Find A Space near ${place}`),
    };
  }

  return {
    text:
      `I found **${pluralLocations(total)}** near **${place}** you could book.` +
      `${more} Here is what each offers:`,
    breakdown,
    link,
  };
};

const answer = async (context: KnowledgeContext): Promise<KnowledgeAnswer> => {
  const { message, geocode } = context;
  const wantsFeatures = mentionsFeatures(message);
  const wantsInstant = wantsInstantApproval(message);
  const place = extractPlace(message);

  if (!place) {
    if (wantsFeatures) {
      return {
        text:
          'I can look that up from the marketplace. Tell me the city or ZIP of the location ' +
          'you mean \u2014 for example ' +
          '_"features of locations in Los Angeles"_ or _"what features do locations in 34741 ' +
          'have"_ \u2014 and I will list what they offer.',
      };
    }
    return {
      text:
        'I can search available locations by city or ZIP. Which area should I look in? ' +
        'For example _"how many locations in Los Angeles"_, _"do you have parking spaces in Long Beach, CA"_, or _"find locations in 34741"_.',
    };
  }

  const point = await geocode(place);
  if (!point) {
    return {
      text:
        `I could not find a place called **${place}**. Try a city with its state ` +
        '(for example _"Dallas, TX"_) or a 5-digit ZIP code.',
    };
  }

  const response = await searchMarketplaceLocations(point.lat, point.lon);
  const visible = (response?.body ?? [])
    .filter((location) => location.visible !== false)
    .sort(
      (left, right) =>
        Number(right.hasInstantApproval === true) - Number(left.hasInstantApproval === true),
    );
  const results = wantsInstant
    ? visible.filter((location) => location.hasInstantApproval === true)
    : visible;

  return describeResults(place, results, wantsFeatures, wantsInstant, point);
};

/**
 * Offered to every audience. Even a supplier asking "do you have parking spaces
 * in Long Beach" is asking about marketplace availability, not their own
 * listings, so the public search is still the honest source.
 */
export const locationSearchQuery: KnowledgeQuery = {
  id: 'marketplaceLocationSearch',
  audiences: ['buyer', 'supplier', 'admin'],
  synonyms: [
    'what features does this location have',
    'what features does this location',
    'what features do locations',
    'what amenities does this location have',
    'what amenities do locations',
    'features of this location',
    'features of locations',
    'location features',
    'how many locations do you have in',
    'how many locations are in',
    'how many locations in',
    'how many spaces are in',
    'find me locations in',
    'find locations in',
    'find me locations near',
    'find locations near',
    'find me a location in',
    'find a location in',
    'find space in',
    'find space near',
    'find a space in',
    'search for locations in',
    'locations near me',
    'locations available in',
    'available locations in',
    'show me locations in',
    'show locations in',
    'do you have parking spaces in',
    'do you have parking spaces near',
    'parking spaces in',
    'parking spaces near',
  ],
  example: 'how many locations are in Los Angeles',
  description:
    'Searches the marketplace of prospective locations by city or ZIP, and lists what ' +
    'they offer (Instant Approval, equipment types, features, capacity). Answers "find ' +
    'locations in X", "do you have parking spaces in X", and "what features does this ' +
    'location have" for a place. This is about locations the user could browse or book ' +
    'from the marketplace, not their own bookings, invoices or payments.',
  answer,
};

export const __testing = {
  extractPlace,
  cleanPlace,
  featureSummary,
  equipmentSummary,
  rowFor,
  describeResults,
  wantsInstantApproval,
};
